import { Eye } from 'lucide-react';
import { useRegion } from '../hooks/useRegion';
import { RegionSelectorLabels } from '../types/regions.types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatDateLong } from '@/shared/utils/format-date.utils';

interface RegionDetailModalProps {
  regionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegionDetailModal({ regionId, open, onOpenChange }: RegionDetailModalProps) {
  const { region, isLoading } = useRegion(open ? regionId : undefined);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return formatDateLong(date);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="region-detail-modal"
        className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Detalle de la región</DialogTitle>
        </DialogHeader>

        {isLoading && <div className="py-8" aria-hidden />}

        {region && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">Nombre</label>
                <p data-testid="detail-region-name" className="text-primary font-medium">
                  {region.name}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Tipo de región
                </label>
                <p data-testid="detail-region-selector" className="text-primary">
                  {RegionSelectorLabels[region.selector as keyof typeof RegionSelectorLabels] ||
                    region.selector}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Territorio
                </label>
                <p data-testid="detail-region-country" className="text-primary">
                  {region.countryNames && region.countryNames.length > 0
                    ? region.countryNames.join(', ')
                    : '-'}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Región padre
                </label>
                <p data-testid="detail-region-parent" className="text-primary">
                  {region.parentName || 'No tiene'}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">Estado</label>
                <div className="mt-1">
                  <span data-testid="detail-region-status">
                    <StatusBadge
                      status={region.isActive ? 'active' : 'inactive'}
                      label={region.isActive ? 'Activo' : 'Deshabilitado'}
                    />
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Fecha de creación
                </label>
                <p data-testid="detail-region-created-at" className="text-primary text-sm">
                  {formatDate(region.createdAt)}
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs text-primary/60 uppercase tracking-wide mb-3 block">
                Regiones hijas
              </label>
              <div
                data-testid="region-children-list"
                className="border border-outline rounded-lg p-3 bg-background"
              >
                {region.children && region.children.length > 0 ? (
                  <div className="space-y-2">
                    {region.children.map((child) => (
                      <div key={child.id} className="flex items-center gap-3 py-1">
                        <Eye className="size-4 text-primary/60" />
                        <span className="text-primary text-sm font-medium">{child.name}</span>
                        <span className="text-primary text-xs bg-primary-background px-2 py-1 rounded">
                          {RegionSelectorLabels[
                            child.selector as keyof typeof RegionSelectorLabels
                          ] || child.selector}
                        </span>
                        <StatusBadge
                          status={child.isActive ? 'active' : 'inactive'}
                          label={child.isActive ? 'Activo' : 'Inactivo'}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p data-testid="region-no-children" className="text-primary text-sm">
                    No hay regiones hijas asociadas.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <UnifiedButton
            data-testid="close-region-detail-modal-btn"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cerrar
          </UnifiedButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
